/**
 * How long to wait before trying a failed write again.
 *
 * Plan: `docs/plans/2026-09-08-next-steps-annotation-write-volume.md` §6.2.
 *
 * The shape is the one the Notes Hub's corpus drain already uses
 * (`notes-hub/index.vue`) — double each time, cap at thirty seconds — with one
 * addition it does not have: FULL JITTER.
 *
 * The jitter is not a refinement, it is the point. Several hundred members
 * share one venue Wi-Fi during a service. When it drops and comes back, every
 * device that was mid-write retries; without jitter they all retry on the same
 * schedule, and the API takes the whole congregation as one wave, at exactly the
 * moment it is already recovering. Spreading each device uniformly across its
 * own window turns that wave into a trickle. It is the one failure mode where
 * our own retry policy could cause the outage it is retrying against.
 */

export const BACKOFF_BASE_MS = 1000;
export const BACKOFF_MAX_MS = 30000;

/**
 * A uniformly random delay in `[0, min(cap, base * 2 ** attempt))`.
 *
 * @param {number} attempt how many attempts have already failed (0-based)
 * @param {() => number} [random] injectable for the specs
 * @returns {number} milliseconds
 */
export function delayFor(attempt, random = Math.random) {
  const ceiling = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** Math.max(0, attempt));
  return Math.floor(random() * ceiling);
}

/**
 * The offline delay. An offline attempt does not count toward the cap (§6.1), so
 * it has no attempt number to grow from — it simply waits out a full window and
 * tries again, which is what makes the backoff timer the wake and removes any
 * need for a "parked offline" state to get stuck in.
 *
 * @param {() => number} [random]
 * @returns {number} milliseconds
 */
export function offlineDelay(random = Math.random) {
  return Math.floor(random() * BACKOFF_MAX_MS);
}

/**
 * What a 429 asked for, in milliseconds, or null when it did not ask.
 * `Retry-After` is either a number of seconds or an HTTP date.
 *
 * @param {object} error an axios error
 * @returns {number|null}
 */
export function retryAfterMs(error) {
  const header =
    error?.response?.headers?.['retry-after'] ?? error?.response?.headers?.['Retry-After'];
  if (header === undefined || header === null || header === '') return null;
  const seconds = Number(header);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const at = Date.parse(header);
  if (Number.isNaN(at)) return null;
  return Math.max(0, at - Date.now());
}
